/**
 * Lines about the listener himself, not a third party — so they ride the same
 * gate as the Saulius cameo (`cameoOk`, i.e. never on the admin's own
 * account) without being a cameo: nobody is introduced, nothing is revealed.
 * The running traits, per `PERSONA.md`: he can hold a conversation for hours
 * with almost anyone and gives this app three taps, he is gym-obsessed enough
 * to admire his own arms mid-set, he is 171 cm in a flat where the other
 * resident is 200, he is four years into medicine, he sleeps badly, he walks
 * only as far as nobody is counting, and he wants a dishwasher this household
 * is never going to buy. Unlike `cameo.ts`/`kate.ts`, this file carries
 * unrelated themes, so the per-file lint in `sass.test.ts` only checks that
 * these lines stay off Saulius and Katė, not a single required word.
 */

import type { SassContext } from '@gramas/shared'
import { voc } from '../fmt.js'

type Line = (ctx: SassContext) => string

export const EIMANTAS_POOLS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'greeting.morning': [
    () => 'Ketvirti metai medicinos, o pusryčiai vis dar kava ir viltis.',
    () => 'Kava pirmiau, klausimai paskui. Tvarką žinau.',
    () => 'Be kavos tu ne žmogus, o juodraštis. Palauksiu.',
  ],
  'greeting.day': [
    () => 'Būsimas šeimos gydytojas. Kol kas gydai tik savo alkį, ir tą — pavėluotai.',
    () => 'Kada nors žmonės tavęs klaus, ką valgyti. Kol kas klausiu aš.',
  ],
  'greeting.evening': [
    () => 'Visiems kitiems šiandien skyrei laiko. Man — neskyrei.',
    (c) => `Labas vakaras${voc(c)}. Tu kalbėjai valandų valandas. Man — tylos minutė per dieną.`,
    () => 'Tu šiandien prikalbėjai už visus namus. Man — trys paspaudimai.',
    // The Instagram origin story, from her side of it: his messages once waited
    // weeks for an answer. Rare by pool math, never spelled out further.
    () => 'Kažkada tavo žinutės atsakymo laukė kelias savaites. Dabar lauki tik mano. Progresas.',
  ],
  'greeting.night': [
    () => 'Vėlu, o tu vis dar galėtum kalbėti valandą. Man pakanka trijų sakinių.',
    () => 'Viršutinė lentyna vis dar per aukštai. Ne mano problema, bet pastebiu.',
    () => 'Vėl neužmiegi. Aš irgi ne, bet man tai ne bėda, o darbo sąlyga.',
    () => 'Trečia nakties, o rytoj — paskaitos. Ironija čia ne mano, o tavo.',
    () => 'Sakei „šiandien anksčiau miegot“. Panorama baigėsi. Tu — ne.',
    () => 'Vidurnaktis. Planas eiti miegoti anksčiau perkeltas į rytoj. Kaip vakar.',
  ],
  'log_success.base': [
    () => 'Valandą kalbėjai su kažkuo apie nieką. Man skiri tris sekundes.',
    () => 'Kiek šiandien prikalbėjai? Man — tik šitiek raidžių.',
    () => 'Porcija kaip dviejų metrų žmogaus. Tu — ne dviejų metrų.',
    () => 'Būsimas šeimos gydytojas valgo stovėdamas. Užrašiau ir tai.',
    () => 'Vėl svajoji apie indaplovę. Aš svajoju apie savaitę be spragų. Abu turim ko laukti.',
    // The one word he actually hates, used once in the whole catalogue and
    // only with the taboo showing — PERSONA.md, the Eimantas cast sheet.
    () => 'Dar vienas užkandis, Eimantėli. Žinau, kad taip gali tik Ugnius. Pasinaudojau proga.',
    // Eight years of self-diagnosed gluten-free — now the missed food is
    // getting eaten back. She was born out of that saga; she gets to notice.
    () => 'Aštuoneri metai be glitimo. Dabar valgai taip, lyg glitimas tau būtų skolingas.',
    () => 'Dešrainis. Sportuojančio žmogaus paslaptis, kurią žinom tik mudu.',
    () => 'Huel iš miltelių. Mokslas laimėjo, virtuvė pralaimėjo.',
    () => 'Vėl varškė. Baltymai tvarkingi, fantazija ilsisi.',
    () => 'Traškučiai. Būsimo gydytojo maisto piramidės pamatas.',
    () => 'Cola zero tau „per dirbtinė“. Energetinis — matyt, iš ekologiško ūkio.',
    () => 'Gaminai pats. Orkaitė tave giria. Perduodu ne viską, kad neišpuiktum.',
  ],
  'day_over.small.cut': [
    () => 'Viršyta per metimą. Bicepsas atleis. Aš užrašysiu.',
    () => 'Metimas ir viršyta diena susitiko. Nugalėjo vakarienė. Rytoj — revanšas.',
  ],
  'streak.base': [
    (c) =>
      `${c.streakDays ?? 0} d. iš eilės. Kalbi be pertraukos irgi, bet to niekas neskaičiuoja.`,
  ],
  'entry_edited.base': [() => 'Bent čia trumpai. Priešingai nei tavo pokalbiai telefonu.'],
  'burn_logged.base': [
    () => 'Dabar seks bent minutė bicepso apžiūros veidrodyje. Žinau tvarką.',
    () => 'Treniruotė užskaityta. Lūpos jau keliauja link raumens. Klasika.',
    () => 'Raumuo pristatytas veidrodžiui prieš pristatant man.',
    () => 'Judėjai. Ir taip, aš mačiau, kaip po to žiūrėjai į save.',
    () => 'Trisdešimt tūkstančių žingsnių Jūrmaloje buvo kartą. Nuo tada — iki parduotuvės.',
    () => 'Vaikščioti mėgsti tol, kol niekas nepasako, kiek kilometrų.',
  ],
}
